// src/pages/ENEMTrainingPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../components/AuthProvider.js';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import InlineMarkupEditor from '../components/InlineMarkupEditor.js';
import AIAnalysisPanel from '../components/AIAnalysisPanel.js';
import Modal from '../components/Modal.js';
import PixPaymentModal from '../components/PixPaymentModal.js';
import essayService from '../services/essayService.js';
import openaiService from '../services/openaiService.js';
import pixPaymentService from '../services/pixPaymentService.js';
import '../styles/ENEMTrainingPage.css';
import { uploadImageToBlob } from '../lib/uploadImageToBlob.js';
import { db } from '../firebase.js';
import {
  collection, getDocs, updateDoc, doc, getDoc, serverTimestamp
} from 'firebase/firestore';

openaiService.setMockMode(false);

const DRAFT_KEY = 'enem_training_draft';
const INTENT_KEY = 'enem_training_intent';
const ONBOARDING_SEEN_KEY = 'enem_onboarding_seen_v2';

// Limits aligned with backend
const MAX_TEXT_CHARS = 8000;
// Give headroom: server rejects base64 length > 3,500,000 chars (~2.6MB binary). 2.45MB keeps us safely under.
const MAX_IMAGE_BYTES = 2.45 * 1024 * 1024; // ~2.45MB
const MAX_IMAGE_BASE64_LEN = 3_500_000;

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'EducationalApplication',
  name: 'Treinamento ENEM - YouSmart',
  description:
    'Plataforma de treinamento para redação do ENEM com correção por inteligência artificial. Pratique com temas reais, receba feedback instantâneo e melhore sua nota.',
  applicationCategory: 'EducationalApplication',
  operatingSystem: 'Web',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'BRL' },
  aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.8', ratingCount: '2450' },
  provider: { '@type': 'Organization', name: 'YouSmart', url: 'https://yousmart.com.br' }
};

const ENEMTrainingPage = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // form
  const [examType, setExamType] = useState(null); // Start with no exam selected
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submissionType, setSubmissionType] = useState(null); // 'text' | 'image' | null
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [themeYearFilter, setThemeYearFilter] = useState('all');
  const [themeSearch, setThemeSearch] = useState('');
  const [customThemeText, setCustomThemeText] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const onboardingTotalSteps = 4;

  // validation UI
  const [titleTouched, setTitleTouched] = useState(false);
  const [contentTouched, setContentTouched] = useState(false);
  const [imageTouched, setImageTouched] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ title: '', content: '', image: '' });

  // image picker modal
  const [showImageModal, setShowImageModal] = useState(false);

  // data / ui
  const [themes, setThemes] = useState([]);
  const [essays, setEssays] = useState([]);
  const [progress, setProgress] = useState(null);
  const [showProgress, setShowProgress] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // credits state
  const [credits, setCredits] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isGrantingAll, setIsGrantingAll] = useState(false);
  // inline analysis
  const [inlineErrors, setInlineErrors] = useState([]);
  const [showMarkups, setShowMarkups] = useState(false);
  const [activeRange, setActiveRange] = useState(null);
  const [analysisKey, setAnalysisKey] = useState(0);

  // ids / delete
  const [currentEssayId, setCurrentEssayId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // modals
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showAnalysisDone, setShowAnalysisDone] = useState(false);
  const [analysisDoneId, setAnalysisDoneId] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingEssayData, setPendingEssayData] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [draftSavedMessage, setDraftSavedMessage] = useState('');
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardNotice, setWizardNotice] = useState('');
  const onboardingProgress = Math.round((onboardingStep / onboardingTotalSteps) * 100);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const imageInputRef = useRef(null);
  const [showWizardFlow, setShowWizardFlow] = useState(false);
  const step2NextButtonRef = useRef(null);

  const startWizardFlow = () => {
    if (!currentUser) {
      setShowAccountModal(true);
      return;
    }
    resetForm();
    setShowWizardFlow(true);
    setWizardStep(1);
  };

  // helpers
  const refreshCredits = useCallback(async (uid) => {
    try {
      const c = await essayService.getUserCredits(uid);
      if (Number.isFinite(c)) setCredits(c);
      return Number.isFinite(c) ? c : null;
    } catch {
      return null;
    }
  }, []);

  const hasAtLeastOneCredit = useCallback(async (uid) => {
    const c = await refreshCredits(uid);
    return typeof c === 'number' && c >= 1;
  }, [refreshCredits]);

  // load
  useEffect(() => {
    (async () => {
      try {
        const th = await essayService.getEssayThemes();
        setThemes(th);
        try {
          const seen = localStorage.getItem(ONBOARDING_SEEN_KEY);
          if (!seen) setShowOnboarding(true);
        } catch (_) {}

        if (currentUser) {
          await essayService.ensureUserDocInitialized(currentUser.uid, 2);
          await refreshCredits(currentUser.uid);
          // Check if current user is admin
          try {
            const meSnap = await getDoc(doc(db, 'users', currentUser.uid));
            const me = meSnap.exists() ? meSnap.data() : null;
            setIsAdmin(me?.role === 'admin');
          } catch (_) {
            setIsAdmin(false);
          }
          const [es, prog] = await Promise.all([
            essayService.getEssaysByStudent(currentUser.uid, 10),
            essayService.getStudentProgress(currentUser.uid),
          ]);
          setEssays(es.map((e) => essayService.formatEssayForDisplay(e)));
          setProgress(prog);
        }
      } catch (err) {
        console.error(err);
        setError('Erro ao carregar dados.');
      }
    })();
  }, [currentUser, refreshCredits]);

  const titleInvalid = titleTouched && !!fieldErrors.title;
  const contentInvalid = submissionType === 'text' && contentTouched && !!fieldErrors.content;
  const imageInvalid = submissionType === 'image' && imageTouched && !!fieldErrors.image;
  const validationReady = submissionType === 'text'
    ? !titleInvalid && !contentInvalid
    : submissionType === 'image'
      ? !titleInvalid && !imageInvalid && !!(imagePreview || imageUrl)
      : false;

  // after login, auto-submit draft if intent set
  useEffect(() => {
    if (!currentUser) return;
    const intent = sessionStorage.getItem(INTENT_KEY);
    const draftRaw = sessionStorage.getItem(DRAFT_KEY);
    if (intent === 'save-and-analyze' && draftRaw) {
      try {
        const draft = JSON.parse(draftRaw);
        setTitle(draft.title || '');
        setContent(draft.content || '');
        setSubmissionType(draft.type || null);
        setImagePreview(draft.imageData || null);
        if (draft.imageUrl) setImageUrl(draft.imageUrl);
        setSelectedTheme(draft.selectedTheme || null);
        setDraftRestored(true);
      } catch {
        sessionStorage.removeItem(DRAFT_KEY);
        sessionStorage.removeItem(INTENT_KEY);
      }
    }
  }, [currentUser]);

  // soft refresh for PIX when user returns from bank app (focus/visibility)
  useEffect(() => {
    if (!currentUser) return;

    const check = async () => {
      try {
        const paid = await pixPaymentService.checkUserPaymentStatus(currentUser.uid);
        const c = await refreshCredits(currentUser.uid);

        if (paid && showPaymentModal && typeof c === 'number' && c >= 1) {
          setShowPaymentModal(false);
          if (pendingEssayData && !isSubmitting) {
            setIsSubmitting(true);
            try {
              await createAndAnalyzeEssayWithCredits(pendingEssayData);
              setPendingEssayData(null);
            } finally {
              setIsSubmitting(false);
              setShowResumePrompt(false);
            }
          }
        }
      } catch {}
    };

    const onFocus = () => { check(); };
    const onVisibility = () => { if (document.visibilityState === 'visible') check(); };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    check();

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [currentUser, showPaymentModal, pendingEssayData, refreshCredits, isSubmitting]);

  // Auto-open file picker when user escolhe "Enviar imagem"
  useEffect(() => {
    if (showImageModal && submissionType === 'image' && !imagePreview && imageInputRef.current) {
      const t = setTimeout(() => {
        try { imageInputRef.current.click(); } catch (_) {}
      }, 150);
      return () => clearTimeout(t);
    }
  }, [showImageModal, submissionType, imagePreview]);

  // theme
  const handleThemeSelect = (th) => {
    setSelectedTheme(th);
    setTitle(th.title);
    setCustomThemeText('');
    if (wizardStep === 2) {
      requestAnimationFrame(() => {
        step2NextButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  };

  const handleSubmissionTypeChange = (value) => {
    setSubmissionType(value);
    if (value === 'image') {
      setShowImageModal(true);
      setImageTouched(true);
    } else {
      setShowImageModal(false);
    }
  };

  const handleExamTypeSelect = (type) => {
    setExamType(type);
    if (wizardStep === 1) goToStep(2);
  };

  const filteredThemes = themes
    .filter((th) => (themeYearFilter === 'all' ? true : `${th.year}` === `${themeYearFilter}`))
    .filter((th) => {
      if (!themeSearch.trim()) return true;
      const q = themeSearch.toLowerCase();
      return th.title.toLowerCase().includes(q) || th.description.toLowerCase().includes(q);
    });

  const availableYears = Array.from(new Set(themes.map((t) => t.year))).sort((a, b) => b - a);

  const renderDescription = (text = '') => {
    const limit = 140;
    if (text.length <= limit) return text;
    return `${text.slice(0, limit)}…`;
  };

  const handleCustomThemeConfirm = () => {
    const clean = customThemeText.trim();
    if (!clean) return;
    const custom = {
      id: 'custom',
      title: clean,
      description: clean,
      year: 'custom'
    };
    setSelectedTheme(custom);
    setTitle(clean);
  };

  const onboardingThemeReady = !!selectedTheme || !!customThemeText.trim();

  const handleOnboardingNext = () => {
    if (onboardingStep === 1) {
      setOnboardingStep(2);
      return;
    }
    if (onboardingStep === 2) {
      if (!onboardingThemeReady) {
        setWizardNotice('Selecione ou digite um tema para continuar.');
        return;
      }
      if (!selectedTheme && customThemeText.trim()) {
        handleCustomThemeConfirm();
      }
      setOnboardingStep(3);
      return;
    }
    if (onboardingStep === 3) {
      setOnboardingStep(4);
      return;
    }
  };

  const handleOnboardingBack = () => {
    setWizardNotice('');
    setOnboardingStep((prev) => Math.max(1, prev - 1));
  };

  const handleFinishOnboarding = () => {
    try { localStorage.setItem(ONBOARDING_SEEN_KEY, '1'); } catch (_) {}
    setShowOnboarding(false);
    setWizardNotice('');
    goToStep(2);
  };

  // autosave draft locally
  useEffect(() => {
    const timer = setTimeout(() => {
      const draft = buildEssayDraft();
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      setLastSavedAt(Date.now());
    }, 800);

    return () => clearTimeout(timer);
  }, [title, content, submissionType, imagePreview, selectedTheme, imageUrl]);

  const handleGrantTenCreditsToAllUsers = async () => {
    if (!isAdmin) {
      alert('Ação restrita a administradores.');
      return;
    }
    const ok = window.confirm(
      'Isto definirá o saldo de TODOS os usuários para 10 créditos.\n\nDeseja continuar?'
    );
    if (!ok) return;

    setIsGrantingAll(true);
    try {
      const usersCol = collection(db, 'users');
      const snap = await getDocs(usersCol);
      const ops = snap.docs.map(async (d) => {
        // Set hard value to 10 (not incremental)
        await updateDoc(doc(db, 'users', d.id), {
          credits: 10,
          updated_at: serverTimestamp(),
          last_topup_at: serverTimestamp()
        });
      });
      await Promise.all(ops);

      // Refresh own credits shown on the CTA label
      if (currentUser) await refreshCredits(currentUser.uid);

      alert('Todos os usuários agora possuem 10 créditos.');
    } catch (err) {
      console.error('Falha ao atualizar créditos de todos os usuários:', err);
      alert('Erro ao definir créditos. Verifique regras de segurança do Firestore.');
    } finally {
      setIsGrantingAll(false);
    }
  };

  const handleSaveDraft = () => {
    const draft = buildEssayDraft();
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    setDraftSavedMessage('Rascunho salvo localmente.');
    setLastSavedAt(Date.now());
  };
  // image helpers
  const readImageFile = (file) => new Promise((resolve, reject) => {
    const rd = new FileReader();
    rd.onload = (ev) => resolve(ev.target.result);
    rd.onerror = reject;
    rd.readAsDataURL(file);
  });


  const handleImagePicked = async (file) => {
     setImageTouched(true);
     if (!file) return;

     if (!file.type || !file.type.startsWith('image/')) {
       setFieldErrors((e) => ({ ...e, image: 'Arquivo inválido (não é imagem).' }));
       setImagePreview(null);
       setImageFile(null);
       return;
     }


    if (file.size > MAX_IMAGE_BYTES) {
       const mb = (MAX_IMAGE_BYTES / (1024 * 1024)).toFixed(2);
       setFieldErrors((e) => ({ ...e, image: `Imagem muito grande. Tamanho máximo: ${mb}MB.` }));
       setImagePreview(null);
       setImageFile(null);
       return;
     }

    const dataUrl = await readImageFile(file);

     // Must be a proper data URL and under server’s base64 length cap
     if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/')) {
       setFieldErrors((e) => ({ ...e, image: 'Imagem inválida. Tente selecionar o arquivo novamente.' }));
       setImagePreview(null);
       setImageFile(null);
       return;
     }
    if (dataUrl.length > MAX_IMAGE_BASE64_LEN) {
       setFieldErrors((e) => ({ ...e, image: 'Imagem muito grande. Use imagem menor que 2.5MB.' }));
       setImagePreview(null);
       setImageFile(null);
       return;
     }

     setImageFile(file);
     setImagePreview(dataUrl);   
    
    setFieldErrors((e) => ({ ...e, image: '' }));

    // Upload to Vercel Blob (preferred for server)
    try {
      setUploadPct(0);
      const res = await uploadImageToBlob(file, {
        folder: 'essays',
        onProgress: (p) => setUploadPct(Math.round(p?.percentage || 0)),
        meta: { purpose: 'enem-analysis' }
      });
      setImageUrl(res.url);
    } catch (err) {
      console.error('Blob upload failed:', err);
      // Keep preview so user can still proceed (server will fall back to base64 if needed)
      setFieldErrors((e) => ({ ...e, image: 'Falha ao enviar a imagem. Tente novamente.' }));
      setImageUrl(null);
    }
   };

  // editor
  const handleContentChange = useCallback((html) => setContent(html), []);
  const handleInlineErrorsUpdate = useCallback((errs) => {
    setInlineErrors(errs || []);
    if (errs?.length) setShowMarkups(true);
  }, []);
  const handleGotoRange = useCallback((range) => {
    setActiveRange(range || null);
    setTimeout(() => setActiveRange(null), 600);
  }, []);

  // validation helpers
  const stripHtml = (h) => {
    const d = document.createElement('div');
    d.innerHTML = h || '';
    return d.textContent || d.innerText || '';
  };

  const validateDraft = (draft) => {
    const errs = { title: '', content: '', image: '' };

    if (!draft.title || draft.title.trim().length < 5) {
      errs.title = 'Título obrigatório (mín. 5 caracteres)';
    }

    if (!draft.type) {
      errs.content = 'Escolha como deseja enviar';
    } else if (draft.type === 'text') {
      const min = 100; // UI min; server min is 50
      const len = (draft.contentPlain || '').trim().length;
      if (!len || len < min) {
        errs.content = `Conteúdo obrigatório (mín. ${min} caracteres)`;
      } else if (len > MAX_TEXT_CHARS) {
        errs.content = `Conteúdo muito longo (máx. ${MAX_TEXT_CHARS} caracteres)`;
      }
    } else if (draft.type === 'image') {
      if (!draft.imageData) {
        errs.image = 'Imagem obrigatória';
      }
    }

    const isValid = !errs.title && !errs.content && !errs.image;
    return { isValid, errs };
  };

  const buildEssayDraft = () => {
    const tmp = document.createElement('div');
    tmp.innerHTML = submissionType === 'text' ? content : '';
    const contentPlain = tmp.textContent || tmp.innerText || '';

    return {
      studentId: currentUser?.uid || null,
      title: title.trim(),
      content: submissionType === 'text' ? content : '',
      contentPlain,
      type: submissionType,
      imageData: submissionType === 'image' ? imagePreview : undefined,
      imageUrl:  submissionType === 'image' ? (imageUrl || null) : null,
      themeId: selectedTheme?.id || null,
      themeName: selectedTheme?.title || customThemeText || '',
      selectedTheme: selectedTheme || (customThemeText ? { id: 'custom', title: customThemeText } : null),
    };
  };

  const goToStep = (step) => {
    setWizardStep(Math.min(totalSteps, Math.max(1, step)));
  };

  const isStepReady = (step) => {
    if (step === 1) return true; // exam always selected
    if (step === 2) return !!selectedTheme || !!customThemeText.trim();
    if (step === 3) return !!submissionType && isStepTwoValid;
    return true;
  };

  const handleNextStep = () => {
    setWizardNotice('');

    if (wizardStep === 1) {
      goToStep(2);
      return;
    }

    if (wizardStep === 2) {
      if (!isStepReady(2)) {
        setWizardNotice('Selecione um tema ou digite o seu para avançar.');
        return;
      }
      goToStep(3);
      return;
    }

    if (wizardStep === 3) {
      if (!submissionType) {
        setWizardNotice('Escolha digitar ou enviar imagem para continuar.');
        return;
      }
      setTitleTouched(true);
      setContentTouched(true);
      setImageTouched(true);
      const draft = buildEssayDraft();
      const validation = validateDraft(draft);
      setFieldErrors(validation.errs);

      if (!validation.isValid) {
        setWizardNotice('Complete os campos obrigatórios antes de revisar e enviar.');
        return;
      }
      goToStep(4);
      return;
    }
  };

  const handlePrevStep = () => {
    setWizardNotice('');
    goToStep(wizardStep - 1);
  };

  const getContentPlain = () => {
    const tmp = document.createElement('div');
    tmp.innerHTML = submissionType === 'text' ? content : '';
    return tmp.textContent || tmp.innerText || '';
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Salvando…';
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 10) return 'Salvo agora';
    if (seconds < 60) return `Salvo há ${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `Salvo há ${minutes}min`;
    const hours = Math.floor(minutes / 60);
    return `Salvo há ${hours}h`;
  };

  const wordCount = React.useMemo(() => {
    const plain = getContentPlain();
    const matches = plain.trim().split(/\s+/).filter(Boolean);
    return matches.length;
  }, [content, submissionType]);

  const WORD_TARGET_MIN = 180;
  const WORD_TARGET_MAX = 500;
  const wordProgress = Math.min(100, Math.round((wordCount / WORD_TARGET_MAX) * 100));
  const isWordTargetMet = wordCount >= WORD_TARGET_MIN && wordCount <= WORD_TARGET_MAX;
  const totalSteps = 4;
  const wizardProgressPct = Math.round((wizardStep / totalSteps) * 100);
  const stepNames = ['Escolha o exame', 'Escolha o tema', 'Formato e escrita', 'Revisar e enviar'];

  const isStepTwoValid = React.useMemo(() => {
    const draft = buildEssayDraft();
    const validation = validateDraft(draft);
    if (!submissionType) return false;
    return validation.isValid;
  }, [title, content, submissionType, imagePreview, selectedTheme, imageUrl]);

  // submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (wizardStep < 4) {
      handleNextStep();
      return;
    }

    setTitleTouched(true);
    setContentTouched(true);
    setImageTouched(true);

    const draft = buildEssayDraft();

    // If not logged in: validate then ask to login
    if (!currentUser) {
      const pre = validateDraft(draft);
      setFieldErrors(pre.errs);
      if (!pre.isValid) return;

      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      sessionStorage.setItem(INTENT_KEY, 'save-and-analyze');
      setShowAccountModal(true);
      return;
    }

    // Logged in: full validation
    const validation = validateDraft({ ...draft, studentId: currentUser.uid });
    setFieldErrors(validation.errs);
    if (!validation.isValid) return;

    if (isSubmitting) return; // prevent duplicate submits

    setIsSubmitting(true);
    await proceedSubmit({ ...draft, studentId: currentUser.uid });
  };

  const proceedSubmit = async (essayData) => {
    try {
      if (!currentUser) {
        try {
          sessionStorage.setItem('enem_training_intent', 'save-and-analyze');
          sessionStorage.setItem('enem_training_draft', JSON.stringify({
            text: essayData && (essayData.text || essayData.contentPlain || ''),
            imageBase64: essayData && (essayData.imageData || null),
            imageUrl: imageUrl || null
          }));
        } catch {}
        setIsSubmitting(false);
        navigate('/login', {
          state: { redirectTo: '/enem-training', intent: 'save-and-analyze' },
          replace: true
        });
        return;
      }

      // HARD GATE: require credits before creating any essay
      const ok = await hasAtLeastOneCredit(currentUser.uid);
      if (!ok) {
        setPendingEssayData(essayData);
        setShowPaymentModal(true);
        setIsSubmitting(false);
        setCredits(0);
        return;
      }

      await createAndAnalyzeEssayWithCredits(essayData);
    } catch (err) {
      console.error('Submit/analyze error:', err);
      setError(err.message || 'Erro ao enviar/analisar.');
      setIsSubmitting(false);
    }
  };

  // credits-aware analysis flow (single source of truth)
  const createAndAnalyzeEssayWithCredits = async (essayData) => {
    try {
      setShowResumePrompt(false);
      // If image submission and we don't have a URL yet (e.g., restored draft), upload now
      if (essayData.type === 'image' && !essayData.imageUrl && imageFile) {
        try {
          const resUp = await uploadImageToBlob(imageFile, {
            folder: 'essays',
            onProgress: (p) => setUploadPct(Math.round(p?.percentage || 0)),
            meta: { purpose: 'enem-analysis' }
          });
          setImageUrl(resUp.url);
          essayData.imageUrl = resUp.url;
        } catch (e) {
          console.warn('Late blob upload failed, will fallback to base64:', e);
        }
      }
      // Create essay only after verifying credits
      const essayId = await essayService.createEssay({
        studentId: currentUser.uid,
        title: essayData.title,
        content: essayData.content,
        type: essayData.type,
        themeId: essayData.themeId,
        themeName: essayData.themeName,
        contentPlain: essayData.contentPlain || '',
        imageUrl: essayData.imageUrl || null,
        examType: examType, // Store exam type with essay
      });
      setCurrentEssayId(essayId);

      const textPlain = essayData.type === 'text' ? (essayData.contentPlain || stripHtml(essayData.content)) : '';
      const imgB64 = essayData.type === 'image' ? (essayData.imageData || null) : null;
      const imgUrl = essayData.type === 'image' ? (essayData.imageUrl || imageUrl || null) : null;
      const analysisResult = await openaiService.analyzeAndPersist({
        essayId,
        essayText: textPlain,
        userId: currentUser.uid,
        imageB64: imgB64,
        imageUrl: imgUrl,
        themeName: essayData.themeName,
        examType: examType, // Pass exam type to API
        setProgress: (msg) => {
          const overlay = document.querySelector('.analysis-text');
          if (overlay) overlay.textContent = msg;
        }
      });

      if (!analysisResult.success) {
        if (analysisResult.reason === 'NO_CREDITS') {
          setPendingEssayData(essayData);
          setShowPaymentModal(true);
          setIsSubmitting(false);
          setCredits(0);
          setWizardNotice('Sem créditos. Finalize o pagamento e continue quando estiver pronto.');
          return;
        } else if (analysisResult.reason === 'MODEL_OUTPUT_INVALID') {
          setError(`Análise inválida: ${analysisResult.message}${analysisResult.retryable ? ' Tente novamente.' : ''}`);
          if (analysisResult.rawResponse) {
            console.warn('Invalid model output:', analysisResult.rawResponse);
          }
        } else {
          setError(analysisResult.message || 'Erro na análise.');
        }
        setIsSubmitting(false);
        return;
      }

      const [es, prog] = await Promise.all([
        essayService.getEssaysByStudent(currentUser.uid, 10),
        essayService.getStudentProgress(currentUser.uid),
      ]);
      setEssays(es.map((e) => essayService.formatEssayForDisplay(e)));
      setProgress(prog);

      if (typeof analysisResult.creditsRemaining === 'number') {
        setCredits(analysisResult.creditsRemaining);
      } else {
        await refreshCredits(currentUser.uid);
      }

      setAnalysisDoneId(essayId);
      setShowAnalysisDone(true);

      resetForm();
      setSuccess(true);

      if (analysisResult.alreadyAnalyzed) {
        setError('');
        console.log('Essay was already analyzed, no credit spent');
      } else {
        console.log(`Analysis completed. Credits remaining: ${analysisResult.creditsRemaining}`);
      }
    } catch (err) {
      console.error('Submit/analyze error:', err);
      setError(err.message || 'Erro ao enviar/analisar.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // LEGACY FUNCTION - kept for compatibility with old flows (not used in payment continuation)
  const createAndAnalyzeEssay = async (essayData) => {
    try {
      const essayId = await essayService.createEssay({
        studentId: currentUser.uid,
        title: essayData.title,
        content: essayData.content,
        type: essayData.type,
        themeId: essayData.themeId,
        themeName: essayData.themeName,
        contentPlain: essayData.contentPlain || '',
      });
      setCurrentEssayId(essayId);

      await runAIAnalysis(essayId, essayData);

      const [es, prog] = await Promise.all([
        essayService.getEssaysByStudent(currentUser.uid, 10),
        essayService.getStudentProgress(currentUser.uid),
      ]);
      setEssays(es.map((e) => essayService.formatEssayForDisplay(e)));
      setProgress(prog);

      try {
        const c = await essayService.getUserCredits(currentUser.uid);
        if (Number.isFinite(c)) setCredits(c);
      } catch {}

      setAnalysisDoneId(essayId);
      setShowAnalysisDone(true);

      resetForm();
      setSuccess(true);
    } catch (err) {
      console.error('Submit/analyze error:', err);
      setError(err.message || 'Erro ao enviar/analisar.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    const c = await refreshCredits(currentUser.uid);
    if (pendingEssayData && typeof c === 'number' && c >= 1) {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        await createAndAnalyzeEssayWithCredits(pendingEssayData);
        setPendingEssayData(null);
        setShowResumePrompt(false);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setError('Pagamento confirmado. Atualizando créditos...');
    }
  };

  const resetForm = () => {
    setTitle('');
    setContent('');
    setSelectedTheme(null);
    setSubmissionType(null);
    setImageFile(null);
    setImagePreview(null);
    setInlineErrors([]);
    setShowMarkups(false);
    setTitleTouched(false);
    setContentTouched(false);
    setImageTouched(false);
    setFieldErrors({ title: '', content: '', image: '' });
    setDraftRestored(false);
    setWizardNotice('');
    setWizardStep(1);
    setCustomThemeText('');
    setOnboardingStep(1);
    setShowResumePrompt(false);
  };

  const runAIAnalysis = async (essayId, data) => {
    try {
      setIsAnalyzing(true);
      const textPlain = data.type === 'text' ? (data.contentPlain || stripHtml(data.content)) : '';
      const imgB64 = data.type === 'image' ? (data.imageData || null) : null;
      const res = await openaiService.analyzeEssay(textPlain, () => {}, imgB64, data.imageUrl || imageUrl || null);
      const parsed = openaiService.parseAnalysisResponse(res.analysis);
      await essayService.updateEssayAnalysis(essayId, parsed);
      if (data.imageUrl || imageUrl) {
        if (essayService.attachImageUrl) await essayService.attachImageUrl(essayId, data.imageUrl || imageUrl);
      }
      setAnalysisKey((k) => k + 1);
    } catch (err) {
      console.error('Falha na análise IA', err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleDeleteEssay = async (essay) => {
    if (!currentUser) {
      sessionStorage.removeItem(DRAFT_KEY);
      sessionStorage.removeItem(INTENT_KEY);
      setShowAccountModal(true);
      return;
    }
    try { await pixPaymentService.checkUserPaymentStatus(currentUser.uid); } catch {}
    if (essay.studentId && essay.studentId !== currentUser.uid) {
      alert('Você não pode excluir esta redação.');
      return;
    }
    const ok = window.confirm(`Tem certeza que deseja excluir a redação "${essay.title}"?`);
    if (!ok) return;
    try {
      setDeletingId(essay.id);
      await essayService.deleteEssay(essay.id);
      const [es, prog] = await Promise.all([
        essayService.getEssaysByStudent(currentUser.uid, 10),
        essayService.getStudentProgress(currentUser.uid),
      ]);
      setEssays(es.map((e) => essayService.formatEssayForDisplay(e)));
      setProgress(prog);
    } catch (err) {
      console.error('Erro ao excluir redação:', err);
      alert('Falha ao excluir. Tente novamente.');
    } finally {
      setDeletingId(null);
    }
  };

  const renderProgressStats = () => {
    if (!progress || !showProgress) return null;
    const barHeight = (score) => ({ height: `${(score / 1000) * 100}%` });
    const trend = progress.progressTrend || [];
    return (
      <section className="progress-stats" aria-label="Estatísticas de progresso">
        <h3>📊 {t('enemTraining.progress')}</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">{progress.totalEssays}</div>
            <div className="stat-label">Enviadas</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{progress.averageScore}</div>
            <div className="stat-label">Média</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{progress.analyzedEssays}</div>
            <div className="stat-label">Analisadas</div>
          </div>
          <div className="stat-card">
            <div className={`stat-number ${progress.recentImprovement >= 0 ? 'positive' : 'negative'}`}>
              {progress.recentImprovement >= 0 ? '+' : ''}
              {progress.recentImprovement}
            </div>
            <div className="stat-label">Melhoria</div>
          </div>
        </div>
        {trend.length > 0 && (
          <div className="progress-chart">
            <h4>Evolução</h4>
            <div className="chart-container">
              {trend.map((p, i) => (
                <div key={i} className="chart-point" title={`${p.title}: ${p.score}`}>
                  <div className="chart-bar" style={barHeight(p.score)}></div>
                  <div className="chart-label">
                    {new Date(p.date).toLocaleDateString('pt-BR', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    );
  };

  const openPreviousCorrection = (essay) => {
    navigate(`/enem-training/essay/${essay.id}`);
  };

  const metaDescription = selectedTheme
    ? `Pratique redação do ENEM sobre "${selectedTheme.title}" com correção por IA. Receba feedback instantâneo e melhore sua nota no ENEM ${new Date().getFullYear()}.`
    : `Treinamento completo para redação do ENEM ${new Date().getFullYear()} com correção por inteligência artificial. Pratique com temas reais, receba feedback detalhado e aumente sua nota.`;

  const renderSubmitLabel = () => {
    if (isSubmitting) return 'Enviando e Analisando…';
    return 'Salvar e Analisar';
  };

  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>{`Treinamento ENEM ${new Date().getFullYear()} - Redação com Correção por IA | YouSmart`}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content="ENEM, redação ENEM, correção redação, inteligência artificial, treinamento ENEM, preparação ENEM, nota 1000, competências ENEM, temas redação ENEM, simulado ENEM, corretor automático, feedback instantâneo, prática redação, vestibular, educação" />
        <meta name="author" content="YouSmart Educação" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yousmart.com.br/enem-training" />
        <meta property="og:title" content="Treinamento ENEM - Redação com Correção por IA | YouSmart" />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content="https://yousmart.com.br/images/enem-training-og.jpg" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="YouSmart" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yousmart.com.br/enem-training" />
        <meta property="twitter:title" content="Treinamento ENEM - Redação com Correção por IA" />
        <meta property="twitter:description" content={metaDescription} />
        <meta property="twitter:image" content="https://yousmart.com.br/images/enem-training-twitter.jpg" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <link rel="canonical" href="https://yousmart.com.br/enem-training" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>


      <main className="enem-training-page" role="main" itemScope itemType="https://schema.org/WebApplication">
        {(isSubmitting || isAnalyzing) && (
          <div className="analysis-overlay" role="alert" aria-live="assertive" aria-busy="true">
            <div className="analysis-spinner" aria-hidden="true" />
            <div className="analysis-text">Processando sua redação… Isso pode levar alguns segundos.</div>
          </div>
        )}

        {/* Header */}
        <header className="page-header" role="banner">
          <h1 itemProp="name">🎯 Treinamento ENEM - Redação com Correção por Inteligência Artificial</h1>
          <p itemProp="description">Pratique redações do ENEM e receba feedback detalhado com IA. Melhore suas competências e alcance a nota 1000!</p>
          <div className="header-actions">
            <button
              className="primary-button big-cta"
              onClick={() => {
                startWizardFlow();
              }}
            >
              Enviar nova redação
            </button>
            <button
              className="progress-toggle"
              onClick={() => setShowProgress(!showProgress)}
              aria-expanded={showProgress}
              aria-controls="progress-stats"
            >
              {showProgress ? 'Ocultar progresso' : 'Ver progresso'}
            </button>
            {isAdmin && (
              <button
                type="button"
                className="progress-toggle admin-credit-button"
                onClick={handleGrantTenCreditsToAllUsers}
                disabled={isGrantingAll}
                aria-busy={isGrantingAll}
                title="Apenas administradores"
              >
                {isGrantingAll ? 'Aplicando…' : '⚙️ Definir 10 créditos para todos'}
              </button>
            )}
          </div>
        </header>

        {renderProgressStats()}

        {/* Instructions + history */}
        <section className="essay-form-section" aria-labelledby="instructions-heading">
          <h2 id="instructions-heading">Como funciona</h2>
          <ol className="instruction-list">
            <li>Escolha o exame e um tema ou digite o seu.</li>
            <li>Digite ou envie a imagem da redação.</li>
            <li>Revise, confirme créditos e envie para correção.</li>
          </ol>
          <button
            className="primary-button big-cta"
            onClick={() => {
              startWizardFlow();
            }}
          >
            Começar nova redação
          </button>
        </section>

        {/* Form flow inside modal */}
        {showWizardFlow && (
          <Modal
            isOpen={showWizardFlow}
            onClose={() => { if (!isSubmitting && !isAnalyzing) setShowWizardFlow(false); }}
          >
            <form onSubmit={handleSubmit} className="essay-form enhanced wizard-form" aria-label="Formulário de redação">
            <div className="wizard-step-title">
              Etapa {wizardStep} de {totalSteps}: {stepNames[wizardStep - 1]}
            </div>
            <div className="wizard-header">
              <div className="wizard-progress compact" aria-label="Progresso do assistente">
                <div className="wizard-progress-track" aria-hidden="true">
                  <div
                    className="wizard-progress-fill"
                    style={{ '--wizard-progress': `${wizardProgressPct}%` }}
                    role="progressbar"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    aria-valuenow={wizardProgressPct}
                  ></div>
                </div>
                <div className="wizard-progress-meta">
                  <span>Etapa {wizardStep} de {totalSteps}</span>
                  <span>{wordCount} palavras</span>
                </div>
              </div>

              {wizardNotice && (
                <div className="info-message subtle" role="status" aria-live="polite">
                  {wizardNotice}
                </div>
              )}
            </div>

            <div className={`wizard-step ${wizardStep === 1 ? 'active' : 'hidden'}`} aria-live="polite">
              <div className="exam-type-selection" role="region" aria-labelledby="exam-type-heading">
                <h3 id="exam-type-heading">Tipo de Exame</h3>
                <p className="muted">Personalize a correção de acordo com o exame escolhido.</p>
                <div className="exam-types-grid" role="radiogroup" aria-label="Selecione o tipo de exame">
                  <label className={`exam-type-card ${examType === 'enem' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="examType"
                      value="enem"
                      checked={examType === 'enem'}
                      onChange={(e) => handleExamTypeSelect(e.target.value)}
                      aria-label="ENEM"
                    />
                    <div className="exam-type-content">
                      <div className="exam-type-name">ENEM</div>
                      <div className="exam-type-desc">5 competências • Nota 0-1000</div>
                    </div>
                  </label>

                  <label className={`exam-type-card ${examType === 'fuvest' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="examType"
                      value="fuvest"
                      checked={examType === 'fuvest'}
                      onChange={(e) => handleExamTypeSelect(e.target.value)}
                      aria-label="FUVEST"
                    />
                    <div className="exam-type-content">
                      <div className="exam-type-name">FUVEST</div>
                      <div className="exam-type-desc">3 critérios • Nota 0-100</div>
                    </div>
                  </label>

                  <label className={`exam-type-card ${examType === 'unicamp' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="examType"
                      value="unicamp"
                      checked={examType === 'unicamp'}
                      onChange={(e) => handleExamTypeSelect(e.target.value)}
                      aria-label="UNICAMP"
                    />
                    <div className="exam-type-content">
                      <div className="exam-type-name">UNICAMP</div>
                      <div className="exam-type-desc">4 critérios • Nota 0-48</div>
                    </div>
                  </label>

                  <label className={`exam-type-card ${examType === 'uerj' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="examType"
                      value="uerj"
                      checked={examType === 'uerj'}
                      onChange={(e) => handleExamTypeSelect(e.target.value)}
                      aria-label="UERJ"
                    />
                    <div className="exam-type-content">
                      <div className="exam-type-name">UERJ</div>
                      <div className="exam-type-desc">4 critérios • Nota 0-20</div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="wizard-nav">
                <div className="credit-chip">
                  <div className="credit-label">Créditos disponíveis</div>
                  <div className="credit-balance">{credits === null ? '—' : credits}</div>
                  <button type="button" className="credit-button" onClick={() => setShowPaymentModal(true)}>
                    Comprar créditos
                  </button>
                </div>
                <button
                  type="button"
                  className="primary-button"
                  onClick={handleNextStep}
                >
                  Próximo: Tema
                </button>
              </div>
            </div>

            <div className={`wizard-step ${wizardStep === 2 ? 'active' : 'hidden'}`} aria-live="polite">
              <div className="theme-selection" role="region" aria-labelledby="theme-heading">
                <div className="theme-heading-row">
                  <div>
                    <h3 id="theme-heading">Escolha um Tema de Redação</h3>
                    <p className="muted">Selecione um tema ou digite o seu para continuar.</p>
                  </div>
                  <div className="status-chip muted">Passo 2 de 4</div>
                </div>

                <div className="theme-filters inline simple">
                  <div className="status-chip muted">{filteredThemes.length} temas disponíveis</div>
                  <div className="filter-group grow">
                    <label className="sr-only" htmlFor="custom-theme">Digite seu tema ou deixe em branco para usar a lista</label>
                    <input
                      id="custom-theme"
                      type="text"
                      placeholder="Digite seu próprio tema (opcional)"
                      value={customThemeText}
                      onChange={(e) => setCustomThemeText(e.target.value)}
                      onBlur={handleCustomThemeConfirm}
                    />
                  </div>
                </div>

                <div className="themes-grid" role="list">
                  {filteredThemes.map((th) => (
                    <article
                      key={th.id}
                      className={`theme-card ${selectedTheme?.id === th.id ? 'selected' : ''}`}
                      onClick={() => handleThemeSelect(th)}
                      role="listitem"
                      tabIndex={0}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleThemeSelect(th)}
                      aria-selected={selectedTheme?.id === th.id}
                    >
                      <div className="theme-year">
                        <time dateTime={th.year}>{th.year}</time>
                      </div>
                      <h4 className="theme-title">{th.title}</h4>
                      <p className="theme-description">{renderDescription(th.description)}</p>
                    </article>
                  ))}
                  {filteredThemes.length === 0 && (
                    <p className="muted">Nenhum tema encontrado. Ajuste os filtros ou digite o seu.</p>
                  )}
                </div>
              </div>

              <div className="wizard-nav">
                <button type="button" className="ghost-button" onClick={handlePrevStep}>
                  Voltar
                </button>
                <button
                  type="button"
                  className="primary-button"
                  onClick={handleNextStep}
                  disabled={!isStepReady(2)}
                  aria-disabled={!isStepReady(2)}
                  ref={step2NextButtonRef}
                >
                  Próximo: Escolher formato
                </button>
                {!isStepReady(2) && (
                  <p className="wizard-hint" role="status" aria-live="polite">
                    Selecione um tema ou digite o seu para continuar.
                  </p>
                )}
              </div>
            </div>

            <div className={`wizard-step ${wizardStep === 3 ? 'active' : 'hidden'}`} aria-live="polite">
              <div className="workspace-header">
                <div className="workspace-progress" style={{ '--progress': `${wordProgress}%` }}>
                  <div className="progress-bar-track" aria-hidden="true">
                    <div className="progress-bar-fill" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={wordProgress}></div>
                  </div>
                  <div className="progress-meta">
                    <span>{wordCount} palavras</span>
                    <span>Meta: {WORD_TARGET_MIN}-{WORD_TARGET_MAX} palavras</span>
                  </div>
                </div>
                <div className="workspace-indicators" role="status" aria-live="polite">
                  <span className="status-chip muted">{formatTimeAgo(lastSavedAt)}</span>
                  <span className={`status-chip ${validationReady ? 'success' : 'warning'}`}>
                    {validationReady ? 'Campos validados' : 'Complete os campos' }
                  </span>
                  <span className={`status-chip ${isWordTargetMet ? 'success' : 'warning'}`}>
                    Faixa de palavras {isWordTargetMet ? 'atingida' : 'ajuste o tamanho'}
                  </span>
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => setIsPromptOpen(!isPromptOpen)}
                    aria-expanded={isPromptOpen}
                    aria-label="Ver diretrizes"
                  >
                    📋 Diretrizes
                  </button>
                </div>
              </div>

              <div className="workspace-grid single">
                {isPromptOpen && (
                  <aside className="prompt-panel open" aria-labelledby="prompt-heading">
                    <div className="prompt-panel__header">
                      <div>
                        <h3 id="prompt-heading">Proposta e Diretrizes</h3>
                        <p className="muted">Consulte o enunciado e a rubrica antes de escrever.</p>
                      </div>
                      <button type="button" className="prompt-toggle" onClick={() => setIsPromptOpen(false)} aria-expanded={isPromptOpen}>
                        Fechar
                      </button>
                    </div>

                    <div className="prompt-card">
                      <h4>{selectedTheme ? selectedTheme.title : 'Selecione um tema'}</h4>
                      <p className="muted">{selectedTheme ? selectedTheme.description : 'Escolha um tema acima para ver o enunciado completo.'}</p>
                      <div className="prompt-guidelines">
                        <h5>Orientações essenciais</h5>
                        <ul>
                          <li>Defina uma tese clara logo na introdução.</li>
                          <li>Organize argumentos em parágrafos coesos com conectivos.</li>
                          <li>Encaminhe uma proposta de intervenção viável e detalhada.</li>
                        </ul>
                      </div>
                      <div className="rubric-card">
                        <h5>Rubrica rápida</h5>
                        <p className="muted">Foque nestes marcadores ao revisar:</p>
                        <ul>
                          <li>Clareza e domínio da norma culta.</li>
                          <li>Repertório sociocultural legitimado e pertinente.</li>
                          <li>Progressão lógica entre parágrafos e conclusão propositiva.</li>
                        </ul>
                      </div>
                    </div>
                  </aside>
                )}

                <div className="editor-panel">
                  <fieldset className="submission-type" role="radiogroup" aria-labelledby="submission-heading">
                    <legend id="submission-heading"><h3>Como deseja enviar?</h3></legend>
                    <div className="type-options buttons-only">
                      <button
                        type="button"
                        className={`type-option button-tile ${submissionType === 'text' ? 'selected' : ''}`}
                        onClick={() => handleSubmissionTypeChange('text')}
                      >
                        💻 Digitar redação
                      </button>
                      <button
                        type="button"
                        className={`type-option button-tile ${submissionType === 'image' ? 'selected' : ''}`}
                        onClick={() => handleSubmissionTypeChange('image')}
                      >
                        📷 Enviar imagem
                      </button>
                    </div>
                  </fieldset>

                  {submissionType === 'text' && (
                    <>
                      <div className="form-group">
                        <label htmlFor="essay-title">Título da Redação</label>
                        <input
                          id="essay-title"
                          type="text"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          onBlur={() => setTitleTouched(true)}
                          aria-invalid={titleInvalid}
                          aria-describedby={titleInvalid ? 'title-error' : 'title-help'}
                          className={titleInvalid ? 'input-invalid' : ''}
                        />
                        {titleInvalid ? (
                          <small id="title-error" className="form-help error-message mt-2">
                            {fieldErrors.title}
                          </small>
                        ) : (
                          <small id="title-help" className="form-help">Digite um título criativo e relacionado ao tema</small>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="essay-content">Conteúdo da Redação</label>
                        {inlineErrors.length > 0 && (
                          <label className="markup-toggle">
                            <input
                              type="checkbox"
                              checked={showMarkups}
                              onChange={(e) => setShowMarkups(e.target.checked)}
                              aria-label="Mostrar correções inline"
                            />
                            Mostrar marcações ({inlineErrors.length})
                          </label>
                        )}
                        <div className="editor-shell">
                          <InlineMarkupEditor
                            content={content}
                            onChange={(html) => {
                              setContent(html);
                              if (!contentTouched) setContentTouched(true);
                            }}
                            inlineErrors={inlineErrors}
                            showMarkups={showMarkups}
                            activeRange={activeRange}
                            placeholder="Escreva sua redação aqui. Lembre-se de seguir a estrutura: introdução, desenvolvimento e conclusão..."
                            aria-label="Editor de redação"
                          />
                        </div>
                        <div className="target-helper">
                          <span className={isWordTargetMet ? 'success-text' : 'warning-text'}>
                            {isWordTargetMet ? 'Faixa de palavras adequada.' : 'Ajuste para ficar entre 180 e 500 palavras.'}
                          </span>
                        </div>
                        {contentInvalid && (
                          <div className="form-help error-message mt-2">
                            {fieldErrors.content}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {submissionType === 'image' && (
                    <>
                      {imagePreview ? (
                        <div className="form-group">
                          <label>Imagem Selecionada</label>
                          <div className="image-preview">
                            <img
                              src={imagePreview}
                              alt="Prévia da redação enviada"
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                          {uploadPct > 0 && uploadPct < 100 && (
                            <div className="form-help mt-2">
                              Enviando imagem… {uploadPct}%
                            </div>
                          )}
                          <div className="inline-actions">
                            <button
                              type="button"
                              className="secondary-button"
                              onClick={() => setShowImageModal(true)}
                            >
                              Trocar imagem
                            </button>
                            {imageInvalid && (
                              <div className="form-help error-message no-margin">
                                {fieldErrors.image}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="form-group">
                          <label>Foto da Redação</label>
                          <button
                            type="button"
                            className="secondary-button"
                            onClick={() => {
                              setShowImageModal(true);
                              setImageTouched(true);
                            }}
                            aria-invalid={imageInvalid}
                          >
                            Selecionar imagem…
                          </button>
                          {imageInvalid && (
                            <div className="form-help error-message mt-2">
                              {fieldErrors.image}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {draftRestored && !success && !error && (
                    <div className="info-message" role="status" aria-live="polite">
                      Seu rascunho foi restaurado. Clique em “Continuar” para revisar antes de enviar.
                    </div>
                  )}
                </div>
              </div>

              <div className="wizard-nav">
                <button type="button" className="ghost-button" onClick={handlePrevStep}>
                  Voltar
                </button>
                <button
                  type="button"
                  className="primary-button"
                  onClick={handleNextStep}
                  disabled={!isStepReady(3)}
                  aria-disabled={!isStepReady(3)}
                >
                  Revisar e enviar
                </button>
                {!isStepReady(3) && (
                  <p className="wizard-hint" role="status" aria-live="polite">
                    Preencha os campos obrigatórios antes de revisar.
                  </p>
                )}
              </div>
            </div>

            <div className={`wizard-step ${wizardStep === 4 ? 'active' : 'hidden'}`} aria-live="polite">
              <div className="review-grid">
                <div className="review-card">
                  <h3>Resumo rápido</h3>
                  <ul className="review-list">
                    <li><strong>Exame:</strong> {examType ? examType.toUpperCase() : 'Selecione um exame'}</li>
                    <li><strong>Tema:</strong> {selectedTheme?.title || 'Selecione um tema'}</li>
                    <li><strong>Tipo de envio:</strong> {submissionType === 'text' ? 'Texto digitado' : 'Foto da redação'}</li>
                    <li><strong>Palavras:</strong> {wordCount} (meta {WORD_TARGET_MIN}-{WORD_TARGET_MAX})</li>
                  </ul>
                  <p className="muted">Revise os campos acima. Se precisar ajustar, volte para a etapa anterior.</p>
                </div>

                <div className="review-card">
                  <h3>Indicadores</h3>
                  <div className="review-chips">
                    <span className="status-chip muted">{formatTimeAgo(lastSavedAt)}</span>
                    <span className={`status-chip ${validationReady ? 'success' : 'warning'}`}>
                      {validationReady ? 'Campos validados' : 'Complete os campos'}
                    </span>
                    <span className={`status-chip ${isWordTargetMet ? 'success' : 'warning'}`}>
                      Faixa de palavras {isWordTargetMet ? 'atingida' : 'ajuste o tamanho'}
                    </span>
                  </div>
                  <div className="review-helper">
                    <h4>Checklist final</h4>
                    <ul>
                      <li>✔️ Introdução com tese clara</li>
                      <li>✔️ Parágrafos coesos e conectivos</li>
                      <li>✔️ Proposta de intervenção detalhada</li>
                      <li>✔️ Tom formal e referências originais</li>
                    </ul>
                  </div>
                </div>
              </div>

              {draftSavedMessage && (
                <div className="info-message" role="status" aria-live="polite">
                  {draftSavedMessage}
                </div>
              )}
              {error && (
                <div className="error-message" role="alert" aria-live="polite">
                  {error}
                </div>
              )}
              {success && (
                <div className="success-message" role="status" aria-live="polite">
                  Redação enviada e análise iniciada com sucesso!
                </div>
              )}

              <div className="wizard-nav">
                <button type="button" className="ghost-button" onClick={handlePrevStep} disabled={isSubmitting}>
                  Voltar e editar
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={handleSaveDraft}
                  disabled={isSubmitting || isAnalyzing}
                >
                  Salvar rascunho
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                >
                  {renderSubmitLabel()}
                </button>
              </div>
            </div>
          </form>
          </Modal>
        )}

        {/* Live analysis helper (kept, still commented as in original) */}
        {/* {(content || imagePreview) && (
          <aside className="analysis-panel" role="complementary" aria-label="Painel de análise em tempo real">
            <AIAnalysisPanel
              key={analysisKey}
              content={submissionType === 'text' ? content : imagePreview}
              isImageAnalysis={submissionType === 'image'}
              onInlineErrorsUpdate={handleInlineErrorsUpdate}
              onRequestJumpToRange={handleGotoRange}
            />
          </aside>
        )} */}

        {/* Recent Essays */}
        {essays.length > 0 && (
          <section className="recent-essays" aria-labelledby="recent-heading">
            <h2 id="recent-heading">📚 Suas Redações Recentes</h2>
            <div className="essays-grid" role="list">
              {essays.map((es) => (
                <article
                  key={es.id}
                  className="essay-card"
                  itemScope
                  itemType="https://schema.org/CreativeWork"
                  role="listitem"
                >
                  <div className="essay-card-row">
                    <div
                      className="essay-card-main"
                      onClick={() => openPreviousCorrection(es)}
                      tabIndex={0}
                      onKeyPress={(e) => e.key === 'Enter' && openPreviousCorrection(es)}
                      role="button"
                      aria-label={`Ver correção da redação: ${es.title}`}
                    >
                      <h3 itemProp="name">{es.title}</h3>
                      <time
                        className="essay-date"
                        dateTime={es.date}
                        itemProp="dateCreated"
                      >
                        {es.formattedDate}
                      </time>
                      <div className="essay-meta">
                        <span className="essay-type" aria-label={`Tipo: ${es.type === 'image' ? 'Imagem' : 'Texto'}`}>
                          {es.type === 'image' ? '📷 Imagem' : '💻 Texto'}
                        </span>
                        <span className="word-count" itemProp="wordCount">
                          {es.wordCount} palavras
                        </span>
                        {es.analysis && typeof es.analysis.totalScore === 'number' && (
                          <span
                            className="score-badge"
                            style={{ backgroundColor: es.scoreColor }}
                            aria-label={`Nota: ${es.analysis.totalScore} de 1000`}
                          >
                            {es.analysis.totalScore}/1000
                          </span>
                        )}
                      </div>
                      <div className="essay-status" aria-label={`Status: ${es.analysis && typeof es.analysis.totalScore === 'number' ? 'Analisada' : 'Aguardando análise'}`}>
                        {es.analysis && typeof es.analysis.totalScore === 'number'
                          ? '✅ Analisada'
                          : '⏳ Aguarda análise'}
                      </div>
                    </div>

                    <div className="essay-actions">
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteEssay(es)}
                        disabled={deletingId === es.id}
                        title="Excluir redação"
                        aria-label={`Excluir redação: ${es.title}`}
                      >
                        {deletingId === es.id ? 'Excluindo…' : 'Excluir'}
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Modal: account needed */}
        <Modal
          isOpen={showAccountModal}
          title="Você precisa de uma conta"
          onClose={() => setShowAccountModal(false)}
          footer={(
            <>
              <button className="btn btn-ghost" onClick={() => setShowAccountModal(false)}>Cancelar</button>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowAccountModal(false); setShowRoleModal(true); }}
              >
                Não tenho conta
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowAccountModal(false);
                  navigate('/login', {
                    state: { redirectTo: '/enem-training', intent: 'save-and-analyze' },
                    replace: true,
                  });
                }}
              >
                Já tenho conta
              </button>
            </>
          )}
        >
          <p>Para salvar e analisar sua redação, entre com sua conta ou crie um cadastro.</p>
        </Modal>

        {/* Modal: choose role */}
        <Modal
          isOpen={showRoleModal}
          title="Como você deseja se cadastrar?"
          onClose={() => setShowRoleModal(false)}
          footer={(
            <>
              <button className="btn btn-ghost" onClick={() => setShowRoleModal(false)}>Voltar</button>
              <button
                className="btn btn-secondary"
                onClick={() =>
                  navigate('/subscribe/teacher', {
                    state: { redirectTo: '/enem-training', intent: 'save-and-analyze' },
                    replace: true,
                  })
                }
              >
                👨‍🏫 Sou Professor(a)
              </button>
              <button
                className="btn btn-primary"
                onClick={() =>
                  navigate('/subscribe/student', {
                    state: { redirectTo: '/enem-training', intent: 'save-and-analyze' },
                    replace: true,
                  })
                }
              >
                👩‍🎓 Sou Estudante
              </button>
            </>
          )}
        >
          <p>Selecione seu perfil para continuar o cadastro:</p>
        </Modal>

        {/* Modal: analysis done */}
        <Modal
          isOpen={showAnalysisDone}
          title="Análise concluída ✅"
          onClose={() => setShowAnalysisDone(false)}
          footer={(
            <>
              <button className="btn btn-primary" onClick={() => {
                setShowAnalysisDone(false);
                if (analysisDoneId) navigate(`/enem-training/essay/${analysisDoneId}`);
              }}>
                Ver resultados da análise
              </button>
              <button className="btn btn-secondary" onClick={() => setShowAnalysisDone(false)}>
                Fechar
              </button>
            </>
          )}
        >
          <p>Sua redação foi analisada. Clique no botão abaixo para ver o painel completo de correções.</p>
        </Modal>

        {/* Modal: PIX Payment */}
        <PixPaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setPendingEssayData(null);
            setIsSubmitting(false);
          }}
          onPaymentSuccess={handlePaymentSuccess}
          user={currentUser}
        />

        {/* Modal: Image Picker */}
        <Modal
          isOpen={showImageModal}
          title="Selecionar Foto da Redação"
          onClose={() => setShowImageModal(false)}
          footer={(
            <>
              <button className="btn btn-ghost" onClick={() => setShowImageModal(false)}>Cancelar</button>
              <label className="btn btn-secondary modal-file-trigger" htmlFor="hidden-image-input">
                Escolher arquivo…
              </label>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setImageTouched(true);
                  if (!imagePreview) {
                    setFieldErrors((e) => ({ ...e, image: 'Imagem obrigatória' }));
                    return;
                  }
                  setFieldErrors((e) => ({ ...e, image: '' }));
                  setShowImageModal(false);
                }}
              >
                Usar esta imagem
              </button>
            </>
          )}
        >
          <div className="modal-upload-grid">
            <input
              id="hidden-image-input"
              type="file"
              accept="image/*"
              className="sr-only-input"
              ref={imageInputRef}
              onChange={async (e) => {
                const file = e.target.files && e.target.files[0];
                if (file) await handleImagePicked(file);
              }}
            />
            <div
              className="upload-dropzone"
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={async (e) => {
                e.preventDefault();
                const f = e.dataTransfer.files && e.dataTransfer.files[0];
                if (f) await handleImagePicked(f);
              }}
            >
              <p className="dropzone-title">
                Arraste uma imagem aqui ou clique em “Escolher arquivo…”
              </p>
              <p className="dropzone-hint">
                Formatos aceitos: PNG, JPG. Tamanho máximo: {(MAX_IMAGE_BYTES / (1024 * 1024)).toFixed(2)}MB.
              </p>
            </div>

            {imagePreview ? (
              <div className="upload-preview">
                <img
                  src={imagePreview}
                  alt="Prévia selecionada"
                  className="upload-preview-image"
                />
              </div>
            ) : (
              <div className="upload-placeholder">
                Nenhuma imagem selecionada ainda.
              </div>
            )}

            {/* Always show image error inside the modal if present */}
            {!!fieldErrors.image && (
              <div className="error-message" role="alert" aria-live="polite">
                {fieldErrors.image}
              </div>
            )}
          </div>
        </Modal>
        {/* Contact / Support */}
        <section
          className="contact-support"
          aria-labelledby="contact-heading"
          itemScope
          itemType="https://schema.org/Organization"
        >
          <h2 id="contact-heading">📞 Precisa de ajuda ou deseja um número maior de correções?</h2>
          <p className="contact-subtext">
            Teve algum problema com o envio, pagamento via PIX ou quer sugerir melhorias? Fale com a gente:
          </p>

          <div className="contact-grid">
            {/* Phone */}
            <article
              className="contact-card"
              itemProp="contactPoint"
              itemScope
              itemType="https://schema.org/ContactPoint"
            >
              <div className="contact-icon" aria-hidden="true">📱</div>
              <div className="contact-body">
                <h3 className="contact-title">Telefone / WhatsApp</h3>
                <p className="contact-detail">
                  <a
                    className="contact-link"
                    href="tel:+552298465565"
                    itemProp="telephone"
                    aria-label="Ligar para suporte"
                  >
                    +55 (22) 99846-5565
                  </a>
                </p>
                <meta itemProp="contactType" content="customer support" />
                <meta itemProp="areaServed" content="BR" />
                <meta itemProp="availableLanguage" content="pt-BR" />
              </div>
            </article>

            {/* Email */}
            <article
              className="contact-card"
              itemProp="contactPoint"
              itemScope
              itemType="https://schema.org/ContactPoint"
            >
              <div className="contact-icon" aria-hidden="true">✉️</div>
              <div className="contact-body">
                <h3 className="contact-title">E-mail</h3>
                <p className="contact-detail">
                  <a
                    className="contact-link"
                    href="mailto:grupoaprendafazendo@gmail.com?subject=Ajuda%20ENEM%20Training"
                    itemProp="email"
                    aria-label="Enviar e-mail para suporte"
                  >
                    grupoaprendafazendo@gmail.com
                  </a>
                </p>
                <meta itemProp="contactType" content="customer support" />
                <meta itemProp="availableLanguage" content="pt-BR" />
              </div>
            </article>
          </div>

          <p className="contact-note">
            Atendimento de segunda a sexta, das 9h às 18h (horário de Brasília).
          </p>
        </section>

      </main>
    </>
  );
};

export default ENEMTrainingPage;
